import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { Router } from '@angular/router';
import { StationaryLogService } from '../../features/stationary-log/services/stationary-log.service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-stationary-log-new',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './stationary-log-new.component.html',
  styleUrl: './stationary-log-new.component.scss',
})
export class StationaryLogNewComponent implements OnInit {
  dailyExpenseNewForm: FormGroup;
  private stationaryLogService = inject(StationaryLogService);
  public stationaryData: any[] | null = null;
  public searchCtrl = new FormControl('');
  public addedItems: any[] = [];
  search: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private datePipe: DatePipe,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.fetchStationaries();
  
    this.searchCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchText) => {
        this.search = searchText ?? '';
        this.fetchStationaries(this.search);
      });
  }
  initForm(): void {
    this.dailyExpenseNewForm = this.fb.group({
      id: [''],
      itemName: ['', Validators.required],
      openingStock: ['', Validators.pattern(/^\d+(\.\d{1,2})?$/)],
      closingStock: ['', Validators.pattern(/^\d+(\.\d{1,2})?$/)],
    });
  }

  onCancel() {
    this.dailyExpenseNewForm.reset();
    this.router.navigate(['/stationary-log']);
  }

  fetchStationaries(search = '') {
    this.stationaryLogService.fetchStationaryItems(search).subscribe({
      next: (data) => {
        this.stationaryData = data.map((stationary: any) => ({
          id: stationary?.id,
          itemName: stationary?.stationoryName || '--',
        }));
      },
      error: (error) => {
        console.error('Error fetching stationaries:', error);
      },
    });
  }

  getTodayDate(): string {
    return this.datePipe.transform(new Date(), 'yyyy-MM-dd') || '';
  }

  allowOnlyInteger(event: KeyboardEvent): void {
    const inputChar = event.key;
    if (!/^[0-9]$/.test(inputChar)) {
      event.preventDefault();
    }
  }
  
  blockInvalidPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    const regex = /^[1-9]\d*$/;
    if (!regex.test(pastedInput)) {
      event.preventDefault();
    }
  }

  getFullDate(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}+00`;
  }

  onItemSelected(event: any) {
    const stationoryId = Number(event.value);
    const todayDate = this.getTodayDate();

    if (!isNaN(stationoryId)) {
      this.fetchOpeningStock(stationoryId, todayDate);
    } else {
      console.error('Invalid Stationory ID:', event.value);
    }
  }

  fetchOpeningStock(stationoryId: number, date: string) {
    if (!stationoryId || !date) {
      console.error('Invalid parameters for fetching opening stock:', {
        stationoryId,
        date,
      });
      return;
    }
    this.stationaryLogService.getOpeningStock(stationoryId, date).subscribe({
      next: (data) => {
        console.log('Opening Stock Data:', data);
        this.dailyExpenseNewForm.patchValue({ openingStock: data ?? 0 });
        // this.dailyExpenseNewForm.patchValue({ openingStock: data });
      },
      error: (error) => {
        // this.dailyExpenseNewForm.reset();
        this.dailyExpenseNewForm.patchValue({ openingStock: null });
        setTimeout(() => {
          this.dailyExpenseNewForm.reset();
        }, 300);
        this.toastr.error(error.message, 'Error');
        console.error('Error fetching opening stock:', error);
      },
    });
  }

  addItem() {
    if (this.dailyExpenseNewForm.valid) {
      const formValue = this.dailyExpenseNewForm.value;

      const selectedItem = this.stationaryData?.find(
        (item) => item.id === formValue.itemName
      );

      if (!selectedItem) {
        this.toastr.error('Invalid item selection', 'Error');
        return;
      }

      const existingItem = this.addedItems.find(
        (item) => item.id === selectedItem.id
      );

      if (existingItem) {
        this.toastr.warning('Item already exists in the table', 'Warning');
        this.dailyExpenseNewForm.reset();
        return;
      }

      const newItem = {
        id: selectedItem.id,
        itemName: selectedItem.itemName,
        openingStock: formValue.openingStock || 0,
        closingStock: formValue.closingStock || 0,
      };

      this.addedItems.push(newItem);
      this.dailyExpenseNewForm.reset();
      this.toastr.success('Item added successfully', 'Success');
    } else {
      this.toastr.error('Please fill in all required fields', 'Error');
    }
  }

  removeItem(index: number) {
    this.addedItems.splice(index, 1);
    this.toastr.success('Item removed successfully', 'Success');
  }

  onSubmit() {
    if (this.addedItems.length === 0) {
      this.toastr.warning(
        'Please add at least one item before submitting',
        'Warning'
      );
      return;
    }

    const createStationoryLogInputs = this.addedItems.map((item) => ({
      stationoryId: item.id,
      date: this.getFullDate(),
      openingStock: Number(item.openingStock) || 0,
      closingStock: Number(item.closingStock) || 0,
    }));

    this.stationaryLogService
      .createStationaryLog(createStationoryLogInputs)
      .subscribe({
        next: (response) => {
          console.log('API Response:', response);
          this.toastr.success('Stationary log created successfully', 'Success');
          this.addedItems = [];
          this.dailyExpenseNewForm.reset();
          this.router.navigate(['/stationary-log']);
        },
        error: (error) => {
          console.error('Error creating stationary log:', error);
          this.toastr.error(error.message || 'Something went wrong', 'Error');
        },
      });
  }
}
